/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { CheckCircle2, Play } from 'lucide-react';
import { useCallback, useMemo, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { type Session } from '../../lib/api/sessions.js';
import { cn } from '../../lib/utils/cn.js';
import { type CalendarDay, formatIst, istDayKey, weekDays } from '../../lib/utils/datetime.js';
import { initialsOf } from '../../lib/utils/initials.js';

const MIN_MS = 60_000;
const DEFAULT_DURATION_MIN = 60;

/** The derived view-model for a Mon–Fri schedule week (memoised in the page). */
export interface WeekModel {
  readonly days: readonly CalendarDay[];
  readonly byDay: ReadonlyMap<string, readonly Session[]>;
  readonly counts: ReadonlyMap<string, number>;
  readonly activeKey: string;
  readonly rangeLabel: string;
}

/** Group sessions by their IST day key, each bucket time-sorted. */
function bucketByIstDay(sessionData: readonly Session[] | undefined): Map<string, Session[]> {
  const byDay = new Map<string, Session[]>();
  for (const s of sessionData ?? []) {
    const key = istDayKey(s.scheduledStart);
    const bucket = byDay.get(key) ?? [];
    bucket.push(s);
    byDay.set(key, bucket);
  }
  for (const bucket of byDay.values()) {
    bucket.sort((a, b) => a.scheduledStart.localeCompare(b.scheduledStart));
  }
  return byDay;
}

/** The selected day if it is in the visible week, else today, else Monday. */
function resolveActiveKey(days: readonly CalendarDay[], selectedKey: string): string {
  if (days.some((d) => d.key === selectedKey)) {
    return selectedKey;
  }
  const todayKey = istDayKey(new Date().toISOString());
  return days.find((d) => d.key === todayKey)?.key ?? days[0]?.key ?? '';
}

/** Bucket the therapist's sessions by IST day and resolve the visible Mon–Fri week. */
export function useWeekModel(
  anchor: Date,
  sessionData: readonly Session[] | undefined,
  selectedKey: string,
): WeekModel {
  return useMemo(() => {
    const week = weekDays(anchor);
    const days = week.slice(0, 5);
    const byDay = bucketByIstDay(sessionData);
    const counts = new Map([...byDay].map(([key, list]) => [key, list.length]));
    const start = formatIst(`${week[0]?.key ?? ''}T00:00:00.000Z`, 'dd MMM');
    const end = formatIst(`${week[4]?.key ?? ''}T00:00:00.000Z`, 'dd MMM yyyy');
    return {
      days,
      byDay,
      counts,
      activeKey: resolveActiveKey(days, selectedKey),
      rangeLabel: `${start} – ${end}`,
    };
  }, [anchor, sessionData, selectedKey]);
}

/** Whole-minute duration of a session window; defaults to 60 when open-ended. */
function durationMin(session: Session): number {
  if (session.scheduledEnd === null) {
    return DEFAULT_DURATION_MIN;
  }
  const ms = new Date(session.scheduledEnd).getTime() - new Date(session.scheduledStart).getTime();
  return ms > 0 ? Math.round(ms / MIN_MS) : DEFAULT_DURATION_MIN;
}

interface DayTabsProps {
  readonly days: readonly CalendarDay[];
  readonly counts: ReadonlyMap<string, number>;
  readonly selectedKey: string;
  readonly onSelect: (key: string) => void;
}

function DayTab({
  day,
  count,
  selected,
  onSelect,
}: {
  readonly day: CalendarDay;
  readonly count: number;
  readonly selected: boolean;
  readonly onSelect: (key: string) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const select = useCallback(() => {
    onSelect(day.key);
  }, [day.key, onSelect]);
  return (
    <button
      type="button"
      onClick={select}
      aria-pressed={selected}
      className={cn(
        'focus-ring flex-1 rounded-xl border px-3 py-3 text-center transition-colors',
        selected
          ? 'border-transparent bg-[var(--accent-strong)] text-white'
          : 'border-slate-200 bg-white text-slate-600 hover:border-[var(--accent)] dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300',
      )}
    >
      <p className="text-sm font-semibold">{formatIst(`${day.key}T00:00:00.000Z`, 'EEE')}</p>
      <p className={cn('text-xs', selected ? 'text-white/80' : 'text-slate-400')}>
        {t('therapist.schedule.daySessions', { count })}
      </p>
    </button>
  );
}

/** Mon–Fri day selector with each day's session count (Figma "Schedule" tabs). */
export function DayTabs({ days, counts, selectedKey, onSelect }: DayTabsProps): JSX.Element {
  return (
    <div className="mb-5 flex gap-3">
      {days.map((day) => (
        <DayTab
          key={day.key}
          day={day}
          count={counts.get(day.key) ?? 0}
          selected={day.key === selectedKey}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
}

const ROW_ACCENT: Readonly<Record<string, string>> = {
  completed: 'border-emerald-400 bg-emerald-50/60 dark:bg-emerald-500/5',
  in_progress: 'border-sky-400 bg-sky-50/60 dark:bg-sky-500/5',
  scheduled: 'border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900',
  cancelled: 'border-slate-200 bg-slate-50 dark:bg-slate-800/40',
  no_show: 'border-slate-200 bg-slate-50 dark:bg-slate-800/40',
};

interface RowProps {
  readonly session: Session;
  readonly clientName: string;
  readonly onReschedule: (session: Session) => void;
  readonly onStart: (id: string) => void;
  readonly starting: boolean;
}

function SessionAction({
  session,
  onStart,
  starting,
}: {
  readonly session: Session;
  readonly onStart: (id: string) => void;
  readonly starting: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const start = useCallback(() => {
    onStart(session.id);
  }, [onStart, session.id]);

  if (session.status === 'completed') {
    return (
      <span className="inline-flex items-center gap-1 text-sm font-medium text-emerald-600">
        <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
        {t('therapist.schedule.done')}
      </span>
    );
  }
  if (session.status === 'in_progress') {
    return (
      <span className="text-sm font-medium text-sky-600">{t('therapist.schedule.active')}</span>
    );
  }
  if (session.status === 'scheduled') {
    return (
      <button
        type="button"
        onClick={start}
        disabled={starting}
        className="focus-ring bg-brand-600 hover:bg-brand-700 inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-60"
      >
        <Play className="h-3.5 w-3.5" aria-hidden="true" />
        {t('therapist.schedule.start')}
      </button>
    );
  }
  return <span className="text-sm text-slate-400">{t(`status.${session.status}`)}</span>;
}

function SessionRow({
  session,
  clientName,
  onReschedule,
  onStart,
  starting,
}: RowProps): JSX.Element {
  const { t } = useTranslation();
  const reschedule = useCallback(() => {
    onReschedule(session);
  }, [onReschedule, session]);
  const movable = session.status === 'scheduled';
  return (
    <li
      className={cn(
        'flex items-center gap-4 rounded-xl border-l-4 p-4',
        ROW_ACCENT[session.status],
      )}
    >
      <div className="w-16 shrink-0 text-center">
        <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
          {formatIst(session.scheduledStart, 'HH:mm')}
        </p>
        <p className="text-xs text-slate-400">
          {t('therapist.schedule.minutes', { n: durationMin(session) })}
        </p>
      </div>
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--accent)] text-xs font-bold text-white">
        {initialsOf(clientName)}
      </span>
      <button
        type="button"
        onClick={movable ? reschedule : undefined}
        disabled={!movable}
        className={cn(
          'focus-ring min-w-0 flex-1 text-left',
          movable ? 'cursor-pointer' : 'cursor-default',
        )}
      >
        <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">
          {clientName}
        </p>
        <p className="truncate text-xs text-slate-400">{session.sessionType ?? '—'}</p>
      </button>
      <SessionAction session={session} onStart={onStart} starting={starting} />
    </li>
  );
}

interface DayListProps {
  readonly sessions: readonly Session[];
  readonly clientNames: ReadonlyMap<string, string>;
  readonly onReschedule: (session: Session) => void;
  readonly onStart: (id: string) => void;
  readonly startingId: string | null;
}

/** The selected day's sessions as a vertical list (Figma "Schedule"). */
export function DayList({
  sessions,
  clientNames,
  onReschedule,
  onStart,
  startingId,
}: DayListProps): JSX.Element {
  const { t } = useTranslation();
  if (sessions.length === 0) {
    return (
      <p className="rounded-2xl border border-slate-200 bg-white py-10 text-center text-sm text-slate-400 dark:border-slate-700 dark:bg-slate-900">
        {t('therapist.schedule.noSessionsDay')}
      </p>
    );
  }
  return (
    <ul className="space-y-3">
      {sessions.map((session) => (
        <SessionRow
          key={session.id}
          session={session}
          clientName={clientNames.get(session.clientId) ?? '—'}
          onReschedule={onReschedule}
          onStart={onStart}
          starting={startingId === session.id}
        />
      ))}
    </ul>
  );
}
